/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx, SerializedStyles } from "@emotion/core"
import { BulkUploadTaskStatus } from "types/delv-platform-api/bulkUploadTask"
import { useTranslation } from "common/utils/i18n"
import _toString from "lodash/toString"

type PropTypes = {
  status: BulkUploadTaskStatus
  totalRowCount?: number
  successRowCount?: number
}

const statusWrapCss = css`
  span {
    font-size: 12px;
  }
`

const statusTextWrap = css`
  border-radius: 4px;
  width: fit-content;
  padding: 4px 8px;
`

const getStatusTextStyle = (
  status: BulkUploadTaskStatus,
): SerializedStyles | "" => {
  if (
    [
      BulkUploadTaskStatus.TaskNew,
      BulkUploadTaskStatus.TaskSubmitted,
      BulkUploadTaskStatus.TaskEventSent,
    ].includes(status)
  )
    return css`
      background-color: #fff9c0;

      span {
        color: #824703;
      }
    `

  if (
    [
      BulkUploadTaskStatus.TaskFailed,
      BulkUploadTaskStatus.TaskInvalid,
      BulkUploadTaskStatus.TaskTimedOut,
    ].includes(status)
  )
    return css`
      background-color: #fff2f0;

      span {
        color: #d42e1c;
      }
    `

  if (
    [
      BulkUploadTaskStatus.TaskSucceeded,
      BulkUploadTaskStatus.TaskPartiallySucceeded,
    ].includes(status)
  )
    return css`
      background-color: #d9fcde;

      span {
        color: #005339;
      }
    `

  return ""
}

const PastUploadStatus = ({
  status,
  totalRowCount,
  successRowCount,
}: PropTypes) => {
  const displayedStatus = useTranslation(`bulkUpload.bulkTask.states.${status}`)
  const displayedPartialSuccessWithCount = useTranslation(
    `bulkUpload.bulkTask.states.TaskPartiallySucceededWithCount`,
    {
      "<x>": _toString(successRowCount || 0),
      "<y>": _toString(totalRowCount || 0),
    },
  )
  const showPartialSuccessWithCount =
    status === BulkUploadTaskStatus.TaskPartiallySucceeded &&
    totalRowCount &&
    successRowCount

  return (
    <div css={statusWrapCss}>
      <div
        css={css`
          ${statusTextWrap} ${getStatusTextStyle(status)}
        `}
      >
        <span>
          {showPartialSuccessWithCount
            ? displayedPartialSuccessWithCount
            : displayedStatus}
        </span>
      </div>
    </div>
  )
}

export default PastUploadStatus
